import {
  commitRPsToVehicleComplete,
  finishAddRPCommitNotification,
  finishAddRPWorkflow,
  getRequiredCircuitsForRPs,
  postAddRPSearch,
  postCommitRPsToVehicle,
  resetAddRPSearch,
  resetAddRPWorkflow,
  RPWF_CANCEL_CIRCUIT_MAPPING,
  RPWF_CANCEL_UNAVAILABLE_PINS,
  RPWF_CANCEL_WORKFLOW,
  RPWF_COMMIT_RP_FAILED,
  RPWF_COMMIT_RP_SUCCESS,
  RPWF_COMMIT_TO_VEHICLE,
  RPWF_CONTINUE_CIRCUIT_MAPPING,
  RPWF_FINISHED_COMMIT,
  RPWF_FINISHED_WORKFLOW,
  RPWF_GET_REQUIRED_CIRCUITS,
  RPWF_PAGINATE_SEARCH_RESULTS,
  RPWF_POST_COMMIT_TO_VEHICLE,
  RPWF_POST_RP_SEARCH,
  RPWF_REMOVE_STAGED_RP,
  RPWF_RESET_WORKFLOW,
  RPWF_SAVE_CTP_MAPPING,
  RPWF_STAGE_COLLECTION,
  RPWF_STAGE_RP,
  RPWF_START_WORKFLOW,
  RPWF_SUBMIT_RP_SEARCH,
  setAddRPIsAddingRP,
  setAddRPIsSearching,
  setAddRPSearchResults,
  setAddRpView,
  setCurrentCollection,
  setIsStagingRPs,
  setSelectedRPs,
  setStagedRPs,
  showConfirmCircuitMappingModal,
  showPinsNotAvailableModal,
} from 'actions/addReferenceParametersWorkflow'
import {
  navigatorReset,
  removeComponentMeta,
  setSelectedIdWithDrawer,
} from 'actions/navigator'
import { apiAction } from 'actions/application'
import { RP_CIRCUITS_RECEIVED } from 'actions/ctp'
import {
  CTPM_CLEAN_UP,
  CTPM_FINISHED_WORKFLOW,
  CTPM_SHOW_CTP_MAPPING_MODAL,
  CTPM_START_WORKFLOW,
  CTPM_CANCEL_CTP_MAPPING,
} from 'actions/ctpMappingWorkflow'
import { getRps, GET_RPS_SUCCESS } from 'actions/referenceParameter'
import { getTruckDetails } from 'actions/truck'
import { basicConflicts } from 'components/AddReferenceParameters/utils'
import {
  compose,
  isEmpty,
  isNil,
  length,
  not,
  path,
  pick,
  prop,
  propOr,
} from 'ramda'
import {
  all,
  call,
  cancel,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import {
  getAddRPWFSearch,
  getAddRPWFSelectedRPs,
  getAddRPWFStagedRPs,
  getAddRPWFStagedRPsUris,
  getAddRPWFVehicleSerialNumber,
  getCommitNote,
} from 'selectors/addReferenceParametersWorkflow'
import { getAvailableCircuits, getVehicleCtpDictionary } from 'selectors/ctp'
import {
  getCtpAssignments,
  getPruneCtpAssignments,
} from 'selectors/ctpMappingWorkflow'
import { getVehicleRouteMatch } from 'selectors/router'
import { getVehicleSubType, selectVehicleBySerial } from 'selectors/truck'
import {
  getReferenceParametersCircuits,
  searchReferenceParameter,
} from 'services/referenceParameters'
import { saveVehicleReferenceParameters } from 'services/vehicles'
import {
  circuitIsInput,
  circuitModule,
  getVehiclePinsForCircuit,
} from 'utils/circuits'
import {
  NOT_SUPPORTED,
  REVIEW_COLLECTION,
  SEARCH,
  SEARCH_TABLE,
  SEARCH_TABLE_COLLECTION,
  ADD_REF_PARAMETERS_WORKFLOW,
} from 'utils/constants'
import { rpCircuitsRequirePinAssignment } from 'utils/truck'
import {
  GET_COLLECTION_REQUEST,
  GET_COLLECTION_SUCCESS,
} from 'actions/collection'
import watchHasServiceRecordDate from 'sagas/vehicleServiceRecord'
import {
  getAddRPView,
  getIsAddingCollection,
} from '../selectors/addReferenceParametersWorkflow'

export default function* watchAddReferenceParameterWorkflow() {
  while (true) {
    yield take(RPWF_START_WORKFLOW)

    const backgroundTasks = yield all([
      fork(watchHasServiceRecordDate),
      fork(watchRPSearch),
      fork(watchAddRequiredCircuits),
      fork(watchSubmitChanges),
      fork(watchGetCollectionSuccess),
    ])

    yield race({
      events: call(watchEvents),
      cancel: take(RPWF_FINISHED_WORKFLOW),
    })

    yield all([cancel(backgroundTasks), call(resetWorkflow)])
  }
}

function* watchEvents() {
  while (true) {
    yield race({
      [RPWF_SUBMIT_RP_SEARCH]: call(watchSubmitRPSearch),
      [RPWF_PAGINATE_SEARCH_RESULTS]: call(watchPaginateRPSearch),
      [RPWF_STAGE_RP]: call(watchStageRp),
      [RPWF_STAGE_COLLECTION]: call(watchStageCollection),
      [RPWF_REMOVE_STAGED_RP]: call(watchRemoveStagedRP),
      [RPWF_CANCEL_UNAVAILABLE_PINS]: call(watchCancelUnAvailablePins),
      [RPWF_COMMIT_TO_VEHICLE]: call(watchCommitToVehicle),
      [RPWF_CANCEL_CIRCUIT_MAPPING]: call(watchCancelCircuitMapping),
      [RPWF_CONTINUE_CIRCUIT_MAPPING]: call(watchContinueCircuitMapping),
      [RPWF_FINISHED_WORKFLOW]: call(watchFinishedWorkflow),
      [RPWF_FINISHED_COMMIT]: call(watchFinishedCommit),
      [RPWF_CANCEL_WORKFLOW]: call(watchCancelWorkflow),
      [RPWF_SAVE_CTP_MAPPING]: call(watchSaveCtpMapping),
      [CTPM_FINISHED_WORKFLOW]: call(watchFinishedCtpMapping),
      [CTPM_CANCEL_CTP_MAPPING]: call(watchCancelCircuitToPinMapping),
    })
  }
}

function* watchFinishedCtpMapping() {
  const { payload = {} } = yield take(CTPM_FINISHED_WORKFLOW)
  const { canceled } = payload
  const isAddingCollection = yield select(getIsAddingCollection)
  const view = yield select(getAddRPView)

  if (isAddingCollection && view === SEARCH_TABLE_COLLECTION) {
    // ended from cancel button of CTP mapping modal
    if (canceled) {
      yield put(setAddRpView(SEARCH_TABLE_COLLECTION))
    } else {
      yield put(setAddRpView(REVIEW_COLLECTION))
    }
    yield put(setIsStagingRPs(false))
  }

  if (view === SEARCH_TABLE) {
    yield put(setIsStagingRPs(false))
  }

  return CTPM_FINISHED_WORKFLOW
}

function* watchSaveCtpMapping() {
  const { selectedRP } = yield take(RPWF_SAVE_CTP_MAPPING)
  const stagedRPs = yield select(getAddRPWFStagedRPs)
  const view = yield select(getAddRPView)

  if (
    isEmpty(stagedRPs) ||
    not(stagedRPs.some((rp) => prop('uri', rp) === prop('uri', selectedRP)))
  ) {
    yield put(setStagedRPs(selectedRP))
  }

  if (view === SEARCH_TABLE) {
    yield put(resetAddRPSearch())
    yield put(setAddRpView(SEARCH))
  }

  return RPWF_SAVE_CTP_MAPPING
}

function* watchCancelCircuitMapping() {
  yield take(RPWF_CANCEL_CIRCUIT_MAPPING)
  yield put(setIsStagingRPs(false))
  yield put(setAddRPIsAddingRP(false))
  yield put(showConfirmCircuitMappingModal(true))
  return RPWF_CANCEL_CIRCUIT_MAPPING
}

function* watchCancelCircuitToPinMapping() {
  yield take(CTPM_CANCEL_CTP_MAPPING)
  yield put(setIsStagingRPs(false))
  return CTPM_CANCEL_CTP_MAPPING
}

function* watchContinueCircuitMapping() {
  yield put(showConfirmCircuitMappingModal(false))
  yield take(RPWF_CONTINUE_CIRCUIT_MAPPING)
  const [serialNumber, assignments, selectedRPs] = yield all([
    select(getAddRPWFVehicleSerialNumber),
    select(getCtpAssignments),
    select(getAddRPWFSelectedRPs),
  ])
  const circuits = yield select(getAvailableCircuits)

  yield put({
    type: CTPM_START_WORKFLOW,
    payload: {
      serialNumber,
      circuits,
      assignments,
      selectedRPs,
    },
  })
  yield put({
    type: CTPM_SHOW_CTP_MAPPING_MODAL,
    payload: {
      visible: true,
    },
  })
  return RPWF_CONTINUE_CIRCUIT_MAPPING
}

function* watchFinishedCommit() {
  yield take(RPWF_FINISHED_COMMIT)
  yield put(finishAddRPWorkflow())
  return RPWF_FINISHED_COMMIT
}

function* watchFinishedWorkflow() {
  yield take(RPWF_FINISHED_WORKFLOW)
  yield put(resetAddRPWorkflow())

  return RPWF_FINISHED_WORKFLOW
}

function* watchCommitToVehicle() {
  yield take(RPWF_COMMIT_TO_VEHICLE)
  const commitNote = yield select(getCommitNote)
  const serialNumber = yield select(getAddRPWFVehicleSerialNumber)

  yield put(postCommitRPsToVehicle(commitNote, serialNumber))
  return RPWF_COMMIT_TO_VEHICLE
}

function* watchCancelUnAvailablePins() {
  yield take(RPWF_CANCEL_UNAVAILABLE_PINS)
  yield put(showPinsNotAvailableModal(false, []))
  return RPWF_CANCEL_UNAVAILABLE_PINS
}

function* watchSubmitRPSearch() {
  const { searchValue, showFilteredResults } = yield take(RPWF_SUBMIT_RP_SEARCH)
  yield put(postAddRPSearch(searchValue, 0, showFilteredResults))
  return RPWF_SUBMIT_RP_SEARCH
}

function* watchPaginateRPSearch() {
  const { startIndex, search, showFilteredResults } = yield take(
    RPWF_PAGINATE_SEARCH_RESULTS
  )
  yield put(postAddRPSearch(search, startIndex, showFilteredResults))
  return RPWF_PAGINATE_SEARCH_RESULTS
}

function* watchCancelWorkflow() {
  yield take(RPWF_CANCEL_WORKFLOW)
  yield put({ type: RPWF_FINISHED_WORKFLOW })
  yield put({ type: CTPM_CLEAN_UP })
  return RPWF_CANCEL_WORKFLOW
}

function* watchStageRp() {
  yield take(RPWF_STAGE_RP)

  yield put(setIsStagingRPs(true))

  const selectedRPs = yield select(getAddRPWFSelectedRPs)
  const serialNumber = yield select(getAddRPWFVehicleSerialNumber)

  yield put(getRequiredCircuitsForRPs(selectedRPs, serialNumber))
  return RPWF_STAGE_RP
}

function* watchStageCollection() {
  yield take(RPWF_STAGE_COLLECTION)
  yield put(setIsStagingRPs(true))

  const selectedRPs = yield select(getAddRPWFSelectedRPs)
  const serialNumber = yield select(getAddRPWFVehicleSerialNumber)
  const isFromCollection = true

  yield put(
    getRequiredCircuitsForRPs(selectedRPs, serialNumber, isFromCollection)
  )
  return RPWF_STAGE_COLLECTION
}

function* watchRemoveStagedRP() {
  yield take(RPWF_REMOVE_STAGED_RP)
  const assignments = yield select(getPruneCtpAssignments)
  yield put({ type: CTPM_CLEAN_UP, assignments })
  return RPWF_REMOVE_STAGED_RP
}

function* watchAddRequiredCircuits() {
  yield takeLatest(RPWF_GET_REQUIRED_CIRCUITS, addRequiredCircuits)
}

function* addRequiredCircuits(action) {
  if (isNil(action)) return

  try {
    const { selectedRPs, serialNumber } = action
    const view = yield select(getAddRPView)
    const isAddingCollection = yield select(getIsAddingCollection)

    yield put(setIsStagingRPs(true))

    const { data: rpCircuits } = yield call(getReferenceParametersCircuits, {
      rps: selectedRPs,
    })

    const hasCircuitsToMap = rpCircuits.some(
      compose(length, prop('circuitDetails'))
    )

    if (!hasCircuitsToMap) {
      yield put(setStagedRPs(selectedRPs))
      yield put(resetAddRPSearch())

      if (!isAddingCollection && view === SEARCH_TABLE) {
        yield put(setAddRpView(SEARCH))
      }

      if (isAddingCollection && view === SEARCH_TABLE_COLLECTION) {
        yield put(setAddRpView(REVIEW_COLLECTION))
      }

      return
    }

    const rpsRequireCircuitMapping = rpCircuits.filter(
      compose(length, prop('circuitDetails'))
    )

    const [ctpDictionary, vehicleSubType, pendingAssignments] = yield all([
      select(getVehicleCtpDictionary, { serialNumber }),
      select(getVehicleSubType, { serialNumber }),
      select(getCtpAssignments),
    ])

    const rpsThatMayRequireAssignment = rpsRequireCircuitMapping.map(
      (rpCircuit) => {
        const { circuitDetails } = rpCircuit
        if (length(circuitDetails)) {
          const {
            circuitsThatRequireMapping,
            pinsAreAvailableForMappingCircuits,
          } = rpCircuitsRequirePinAssignment(
            circuitDetails,
            ctpDictionary,
            pendingAssignments,
            vehicleSubType
          )
          return {
            ...rpCircuit,
            circuitsThatRequireMapping,
            pinsAreAvailableForMappingCircuits,
          }
        }
        return rpCircuit
      }
    )

    const rpsThatRequireCircuitMapping = rpsThatMayRequireAssignment.filter(
      (rp) => Boolean(length(prop('circuitsThatRequireMapping', rp)))
    )

    const numberOfRpsThatRequireCircuitMapping =
      rpsThatRequireCircuitMapping.length

    const noCircuitWithOutAvailablePins = rpsThatRequireCircuitMapping
      .map(prop('pinsAreAvailableForMappingCircuits'))
      .includes(false)

    const rpCircuitsThatRequireMappingWithModules =
      rpsThatRequireCircuitMapping.map((rpCircuit) => {
        const { circuitsThatRequireMapping, refParameterNumber } = rpCircuit
        const matchedRp = selectedRPs.find(
          (rp) => rp.refParameterNumber === refParameterNumber
        )
        const circuits = circuitsThatRequireMapping.map((circuit) => ({
          ...circuit,
          input: circuitIsInput(circuit),
          module: circuitModule(circuit),
        }))
        return {
          circuits,
          rpUri: matchedRp.uri,
        }
      })

    if (numberOfRpsThatRequireCircuitMapping > 0) {
      // if any pins are not available, bail on whole thing
      if (!noCircuitWithOutAvailablePins) {
        yield put({
          type: RP_CIRCUITS_RECEIVED,
          rps: selectedRPs,
          circuits: rpCircuitsThatRequireMappingWithModules,
        })
        yield put(showConfirmCircuitMappingModal(true))
      } else {
        yield call(handleUnAvailablePins, {
          serialNumber,
          rpCircuits,
        })
      }
      // exit out due to going into CTP mapping
      return
    }

    yield put(setIsStagingRPs(false))
    yield put(setStagedRPs(selectedRPs))
    yield put(resetAddRPSearch())

    if (isAddingCollection && view === SEARCH_TABLE_COLLECTION) {
      yield put(setAddRpView(REVIEW_COLLECTION))
    }

    return
  } catch (e) {
    throw new Error(e)
  }
}

function* handleUnAvailablePins({ serialNumber, rpCircuits }) {
  const [ctpDictionary, vehicleSubType] = yield all([
    select(getVehicleCtpDictionary, { serialNumber }),
    select(getVehicleSubType, { serialNumber }),
  ])
  const cptDictionaryByCircuitNumber = Object.values(ctpDictionary).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.circuitNumber]: curr.uri,
    }),
    {}
  )
  const circuitsByRpNotOnVehicle = rpCircuits.map(
    ({ circuitDetails, refParameterNumber, releaseNumber }) => ({
      circuits: circuitDetails.filter(
        ({ circuitNumber }) => !cptDictionaryByCircuitNumber[circuitNumber]
      ),
      refParameterNumber,
      releaseNumber,
    })
  )

  const conflictingCircuitsByRp = circuitsByRpNotOnVehicle.map(
    ({ circuits, refParameterNumber, releaseNumber }) => ({
      circuits: circuits.map((c) => ({
        ...c,
        availablePins: !!getVehiclePinsForCircuit(
          c,
          ctpDictionary,
          [],
          vehicleSubType
        ),
        input: circuitIsInput(c),
        module: circuitModule(c),
      })),
      refParameterNumber,
      releaseNumber,
    })
  )

  yield put(
    showPinsNotAvailableModal(true, conflictingCircuitsByRp, serialNumber)
  )
}

function* watchRPSearch() {
  yield takeLatest(RPWF_POST_RP_SEARCH, rpSearch)
}

function* rpSearch(action) {
  if (isNil(action)) return
  try {
    yield put(setAddRPIsSearching(true))
    yield put(setAddRpView(SEARCH_TABLE))
    const { startIndex = 1, search, showFilteredResults } = action
    const {
      params: { id: serialNumber },
    } = yield select((state) => getVehicleRouteMatch(state))
    const previous = pick(
      ['startIndex', 'search', 'count'],
      yield select((state) => getAddRPWFSearch(state))
    )
    const postBody = {
      ...previous,
      compatibleWith: serialNumber,
      search,
      filterConflicts: showFilteredResults,
      startIndex: search === prop('search', previous) ? startIndex : 1,
    }
    const {
      data: { referenceParameters = [], total },
    } = yield call(searchReferenceParameter, postBody)

    // Guard: if the user cleared the search while this request was in-flight,
    // the view was reset to SEARCH. Discard results rather than overwrite the
    // reset state.
    const currentView = yield select(getAddRPView)
    if (currentView !== SEARCH_TABLE) return

    yield put(
      setAddRPSearchResults({ ...postBody, referenceParameters, total })
    )
  } catch (e) {
    throw new Error(e)
  } finally {
    yield put(setAddRPIsSearching(false))
  }
}

function* watchSubmitChanges() {
  yield takeLatest(RPWF_POST_COMMIT_TO_VEHICLE, commitChanges)
}

const commitRPsToVehicleAction = (
  serialNumber,
  referenceParameters,
  ctpModels,
  notes
) =>
  apiAction({
    asyncFn: saveVehicleReferenceParameters,
    args: { serialNumber, referenceParameters, ctpModels, notes },
    successActionType: RPWF_COMMIT_RP_SUCCESS,
    failureActionType: RPWF_COMMIT_RP_FAILED,
  })

function* commitChanges(action) {
  if (isNil(action)) return

  try {
    const { notes, serialNumber } = action
    const [referenceParameters, modals] = yield all([
      select(getAddRPWFStagedRPsUris),
      select(getCtpAssignments),
    ])

    const ctpModels = modals.reduce(
      (acc, curr) => [
        ...acc,
        {
          ...curr,
          pin: path(['pin', 'location'], curr),
        },
      ],
      []
    )

    yield put(
      commitRPsToVehicleAction(
        serialNumber,
        referenceParameters,
        ctpModels,
        notes
      )
    )

    const winningAction = yield race({
      [RPWF_COMMIT_RP_SUCCESS]: take(RPWF_COMMIT_RP_SUCCESS),
      [RPWF_COMMIT_RP_FAILED]: take(RPWF_COMMIT_RP_FAILED),
    })

    const { code, errorMessage } = propOr(
      {},
      RPWF_COMMIT_RP_FAILED,
      winningAction
    )

    if (errorMessage) {
      yield put(finishAddRPCommitNotification(code, errorMessage))
    } else {
      yield all([
        put(getTruckDetails({ serialNumber })),
        put(commitRPsToVehicleComplete(serialNumber)),
        put(finishAddRPCommitNotification()),
      ])
    }
  } catch (e) {
    throw new Error(e)
  } finally {
    yield call(resetWorkflow)
    yield put(navigatorReset())
    yield put(removeComponentMeta([ADD_REF_PARAMETERS_WORKFLOW]))
    yield put(setSelectedIdWithDrawer(null))
  }
}

function* resetWorkflow() {
  yield put(resetAddRPWorkflow())
  yield put({ type: CTPM_CLEAN_UP, assignments: [] })
  return RPWF_RESET_WORKFLOW
}

export function* watchGetCollectionSuccess() {
  yield takeEvery(GET_COLLECTION_REQUEST, handleGetGroup)
}

function* handleGetGroup() {
  const { collection, data, serialNumber } = yield take(GET_COLLECTION_SUCCESS)
  const { partUris } = data

  yield put(setAddRPIsSearching(true))
  yield put(setAddRpView(SEARCH_TABLE_COLLECTION))
  yield put(setCurrentCollection(collection))
  yield put(getRps(partUris, serialNumber))

  const {
    data: { referenceParameters },
  } = yield take(GET_RPS_SUCCESS)

  // check rps against partUris, add missing as incompatable rps
  const rps = partUris.map((uri) => {
    const match = referenceParameters.find((rp) => rp.uri.includes(uri))
    if (match) {
      return match
    }
    return {
      uri,
      refParameterNumber: uri.split('/')[2],
      release: uri.split('/')[3],
      status: NOT_SUPPORTED,
    }
  })

  const { referenceParameters: alreadyOn } = yield select(
    selectVehicleBySerial,
    { serialNumber }
  )
  const alreadyStaged = yield select(getAddRPWFStagedRPs)

  // incompatable rps cannot be selected, start selected list with
  const rpsThatCanBeSelected = referenceParameters.filter((rp) => {
    // check for a basic conflict, bail early with conflict message
    const basicCheckValues = {
      status: rp.status,
      uri: rp.uri,
      refParameterNumber: rp.refParameterNumber,
      release: rp.release,
      masterNumber: rp.masterNumber,
      alreadyOn,
      alreadyStaged,
    }

    const conflictMessage = basicConflicts(basicCheckValues)

    return Boolean(not(conflictMessage))
  })

  yield put(setSelectedRPs(rpsThatCanBeSelected))
  yield put(
    setAddRPSearchResults({
      ...data,
      referenceParameters: rps,
      startIndex: 1,
      search: '',
      count: rps.length,
      total: rps.length,
      compatibleWith: serialNumber,
      filterConflicts: false,
    })
  )
  yield put(setAddRPIsSearching(false))
}
